<template>
  <div class="registration-form__step registration-form__step-address">
    <div class="registration-form__step__container">
      <div class="data">
        <template v-if="sectorRequiresInput">
          <div
            v-if="
              (requiresInputForCraftsman || requiresInputForRetailer) &&
              selectSubTargetGroupList.length > 0
            "
            class="data-item retailer-list"
          >
            <select-input-fi
              :label="subTargetGroupTitle"
              :value="subTargetGroup"
              :options="selectSubTargetGroupList"
              :rules="subTargetGroupRule"
              data-test="sub-target-group"
              :data-test-2="subTargetGroup"
              required
              @input="subTargetGroupListChanged"
              @open="enableSubTargetGroupValidation = true"
            />
          </div>

          <text-input-fi
            ref="companyInput"
            v-model.trim="company"
            class="data-item company"
            name="company"
            data-test="company"
            :rules="rules.requiredAfterTrimmed"
            :maxlength="120"
            :label="fields.companyNameLabel?.value"
            required
          />

          <div
            v-if="!registrationWithoutTargetGroup"
            :class="{
              'data-item customer-number': true,
              'tooltip-wrapper': fields.customerIDInfoText?.value,
            }"
          >
            <text-input-fi
              v-model.trim="customerNumber"
              name="customerNumber"
              data-test="customer-number"
              :rules="rules.noTags"
              :maxlength="13"
              :label="fields.customerIDLabel?.value"
            />
            <tooltip-fi
              v-if="fields.customerIDInfoText?.value"
              class="tooltip"
              :tooltip="fields.customerIDInfoText.value"
            />
          </div>
        </template>

        <text-input-fi
          ref="streetInput"
          v-model.trim="street"
          class="data-item street"
          name="street"
          data-test="street"
          :rules="rules.street"
          :maxlength="120"
          :label="fields.streetLabel?.value"
          required
        />

        <text-input-fi
          ref="streetNumberInput"
          v-model.trim="streetNumber"
          class="data-item street-number"
          name="streetNumber"
          data-test="street-number"
          :rules="rules.streetnumber"
          :maxlength="20"
          :label="fields.streetNumberLabel?.value"
          required
        />

        <text-input-fi
          ref="zipcodeInput"
          v-model.trim="zipcode"
          class="data-item zipcode"
          name="zipcode"
          data-test="zipcode"
          :rules="enableZipcodeValidation ? validateZip(zipcode) : []"
          :maxlength="20"
          :label="fields.zipCodeLabel?.value"
          required
          @keydown="enableZipcodeValidationDefault = false"
          @blur="zipCodeBlurred"
        />

        <text-input-fi
          ref="cityInput"
          v-model.trim="city"
          class="data-item city"
          name="city"
          data-test="city"
          :rules="rules.city"
          :maxlength="120"
          :label="fields.cityLabel?.value"
          required
        />

        <div
          v-if="countryList.length > 0"
          class="data-item retailer-list"
        >
          <select-input-fi
            :label="fields.countryLabel?.value"
            :value="country"
            :options="countryList"
            :rules="enableCountryValidation ? rules.country : []"
            :data-test-2="country"
            data-test="country"
            required
            @input="countryListChanged"
            @open="enableCountryValidation = true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SelectInputFi, TextInputFi, TooltipFi } from 'atoms';
import { useRegistrationStore } from 'store/registration';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

import { useCountryZipValidation } from '@/composables/countryZipValidation';
import { useFields } from '@/composables/fields';
import { useValidators } from '@/composables/validators';
import { getSelectList } from '@/utils/fieldTransformations';

defineOptions({ name: 'RegistrationStepAddress' });

const emit = defineEmits<{
  'valid-changed': [value: boolean];
}>();

const fields = useFields('MyAccountRegisterFields');

const registrationStore = useRegistrationStore();
const { rules } = useValidators();
const { setCountryZipValidation, validateZip } = useCountryZipValidation(fields);

const isValid = ref(false);
const countryList = ref<any[]>([]);
const enableSubTargetGroupValidation = ref(false);
const enableCountryValidation = ref(false);
const enableZipcodeValidation = ref(false);
const enableZipcodeValidationDefault = ref(true);

// Template refs — named to match ref="..." in template but distinct from computed below
const companyInputRef = useTemplateRef('companyInput');
const streetInputRef = useTemplateRef('streetInput');
const streetNumberInputRef = useTemplateRef('streetNumberInput');
const zipcodeInputRef = useTemplateRef('zipcodeInput');
const cityInputRef = useTemplateRef('cityInput');

const targetGroup = computed(() => registrationStore.targetGroup);
const registrationWithoutTargetGroup = computed(
  () => fields.registrationType?.value === 'WithoutTargetGroup',
);
const sectorRequiresInput = computed(() => targetGroup.value !== 'GROUP-diy');
const requiresInputForCraftsman = computed(() => targetGroup.value === 'GROUP-craftsman');
const requiresInputForRetailer = computed(() => targetGroup.value === 'GROUP-retailer');

const selectSubTargetGroupList = computed(() => {
  if (requiresInputForCraftsman.value) {
    const [, subTargetGroupList] = getSelectList(fields, 'subTargetGroupCraftList', [], true);
    return subTargetGroupList || [];
  }
  if (requiresInputForRetailer.value) {
    const [, subTargetGroupList] = getSelectList(fields, 'subTargetGroupRetailerList', [], true);
    return subTargetGroupList || [];
  }
  return [];
});

const subTargetGroupTitle = computed(() => {
  if (requiresInputForCraftsman.value) {
    return fields.subTargetGroupCraftLabel?.value;
  }
  return fields.subTargetGroupRetailerLabel?.value;
});

const subTargetGroupRule = computed(() => {
  let rule: any[] = [];
  if (requiresInputForCraftsman.value && enableSubTargetGroupValidation.value) {
    rule = rules.value.craft;
  }
  if (requiresInputForRetailer.value && enableSubTargetGroupValidation.value) {
    rule = rules.value.retailer;
  }
  return rule;
});

const subTargetGroup = computed({
  get() {
    const val = registrationStore.subTargetGroup;
    return val?.length ? val : null;
  },
  set(val: string) {
    const title = selectSubTargetGroupList.value.find((group) => group.value === val)?.title;
    registrationStore.subTargetGroup = val;
    registrationStore.subTargetGroupTitle = title || val;
    validate();
  },
});

const country = computed({
  get() {
    return registrationStore.country;
  },
  set(val: string) {
    registrationStore.country = val;
    validate();
  },
});

const company = computed({
  get() {
    return registrationStore.company;
  },
  set(val: string) {
    registrationStore.company = val;
    validate();
  },
});

const customerNumber = computed({
  get() {
    return registrationStore.customerNumber;
  },
  set(val: string) {
    registrationStore.customerNumber = val;
    validate();
  },
});

const street = computed({
  get() {
    return registrationStore.street;
  },
  set(val: string) {
    registrationStore.street = val;
    validate();
  },
});

const streetNumber = computed({
  get() {
    return registrationStore.streetNumber;
  },
  set(val: string) {
    registrationStore.streetNumber = val;
    validate();
  },
});

const zipcode = computed({
  get() {
    return registrationStore.zipcode || '';
  },
  set(val: string) {
    registrationStore.zipcode = val.toLocaleUpperCase();
    validate();
  },
});

const city = computed({
  get() {
    return registrationStore.city;
  },
  set(val: string) {
    registrationStore.city = val;
    validate();
  },
});

const validateAddress = computed({
  get() {
    return registrationStore.validateAddress;
  },
  set(val: boolean) {
    registrationStore.validateAddress = val;
  },
});

const validate = async () => {
  // the errorBuckets are not computed, so we have no guarantee they are updated yet
  await nextTick();

  if (
    streetInputRef.value &&
    streetNumberInputRef.value &&
    zipcodeInputRef.value &&
    cityInputRef.value
  ) {
    let isSubTargetGroupValid = true;
    if (
      (requiresInputForCraftsman.value || requiresInputForRetailer.value) &&
      selectSubTargetGroupList.value.length > 0
    ) {
      isSubTargetGroupValid = Boolean(subTargetGroup.value);
    }

    let isCompanyValid = true;
    if (sectorRequiresInput.value) {
      isCompanyValid = Boolean(
        company.value !== '' && companyInputRef.value && !companyInputRef.value.errorBucket.length,
      );
    }

    isValid.value = Boolean(
      isSubTargetGroupValid &&
      isCompanyValid &&
      street.value !== '' &&
      !streetInputRef.value.errorBucket.length &&
      streetNumber.value !== '' &&
      !streetNumberInputRef.value.errorBucket.length &&
      zipcode.value !== '' &&
      !zipcodeInputRef.value.errorBucket.length &&
      enableZipcodeValidation.value &&
      city.value !== '' &&
      !cityInputRef.value.errorBucket.length &&
      country.value &&
      country.value !== '',
    );
  } else {
    isValid.value = false;
  }
  validateAddress.value = false;
  emit('valid-changed', isValid.value);
};

const zipCodeBlurred = async () => {
  enableZipcodeValidation.value = true;
  await nextTick();
  validate();
};

const subTargetGroupListChanged = (value: string) => {
  subTargetGroup.value = value;
};

const countryListChanged = (value: string) => {
  setCountryZipValidation(value);
  country.value = value;
};

watch(validateAddress, (value) => {
  if (value) {
    validate();
  }
});

onMounted(() => {
  const [defaultCountry, list] = getSelectList(fields, 'countryList');
  countryList.value = list?.sort((a, b) => a.name.localeCompare(b.name));
  if (!country.value && defaultCountry?.value) {
    countryListChanged(defaultCountry.value);
  }
  validate();
});

// created logic
if (zipcode.value) {
  enableZipcodeValidation.value = true;
}
</script>

<style scoped src="./registration-step-address" />
